import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '@nutrix/data-access-auth';
import { AgendaDataService } from '@nutrix/data-access-agenda';
import { AgendaAppointment, SchedulableUser } from '@nutrix/agenda-model';
import { Company } from '@nutrix/patients-model';
import { UiAgendaComponent } from '@nutrix/ui-agenda';
import { CallService, CallsDataService, WhatsAppCallHistory } from '@nutrix/data-access-calls';

interface UpcomingCall {
  appointment: AgendaAppointment;
  contactHealthId: string;
  name: string;
  startsOnUTC: string;
  permission: 'Approved' | 'Requested' | 'None';
  permissionLoading: boolean;
}

/**
 * WhatsApp calls workspace inside the agenda: upcoming WhatsApp consultations the
 * doctor can dial straight from here, quick stats, and the full call history with
 * recording playback (recordings restricted to doctors/admins).
 */
@Component({
  selector: 'nutrix-call-history',
  standalone: true,
  imports: [CommonModule, TableModule, TagModule, ButtonModule, TooltipModule, SelectModule, FormsModule],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './call-history.component.html',
  styleUrl: './call-history.component.css',
})
export class CallHistoryComponent implements OnInit {
  readonly #data = inject(CallsDataService);
  readonly #agenda = inject(AgendaDataService);
  readonly #messageService = inject(MessageService);
  readonly #dialogService = inject(DialogService);
  readonly call = inject(CallService);
  readonly #currentUser = inject(AuthService).loadCurrentUser();

  readonly history = signal<WhatsAppCallHistory[]>([]);
  readonly upcoming = signal<UpcomingCall[]>([]);
  readonly loadingHistory = signal(false);
  readonly loadingUpcoming = signal(false);
  readonly playingCallId = signal<string | null>(null);

  readonly stats = computed(() => {
    const h = this.history();
    const completed = h.filter((c) => c.callStatus === 'Completed');
    const missed = h.filter((c) => c.callStatus === 'Missed' || c.callStatus === 'Failed');
    const totalSeconds = completed.reduce((sum, c) => sum + (c.durationSeconds || 0), 0);
    return {
      total: h.length,
      completed: completed.length,
      missed: missed.length,
      minutes: Math.round(totalSeconds / 60),
    };
  });

  /** Upcoming calls grouped by local calendar day, oldest day first. */
  readonly upcomingByDate = computed(() => {
    const groups = new Map<string, { key: string; date: Date; items: UpcomingCall[] }>();
    for (const u of this.upcoming()) {
      const d = new Date(u.startsOnUTC);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      let group = groups.get(key);
      if (!group) {
        group = { key, date: d, items: [] };
        groups.set(key, group);
      }
      group.items.push(u);
    }
    return Array.from(groups.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  });

  dateGroupLabel(date: Date): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Mañana';
    return '';
  }

  readonly canPlayRecordings = ((): boolean => {
    const u = this.#currentUser;
    if (!u) return false;
    if (u.admin || u.nutrixAdmin) return true;
    return u.doctor && !u.externalUser && !u.profissional;
  })();

  // Same privilege gate: only Nutrix doctors + admins may browse other people's history.
  readonly canFilterHistory = this.canPlayRecordings;

  // history filters
  readonly companies = signal<Company[]>([]);
  readonly companyUsers = signal<SchedulableUser[]>([]);
  readonly selectedCompanyId = signal<string | null>(null);
  readonly selectedUserId = signal<string | null>(null);

  #audio: HTMLAudioElement | null = null;
  #objectUrl: string | null = null;

  ngOnInit(): void {
    this.loadHistory();
    this.loadUpcoming();
    if (this.canFilterHistory) {
      this.#agenda
        .getSchedulableCompanies()
        .pipe(catchError(() => of([] as Company[])))
        .subscribe((c) => this.companies.set(c ?? []));
    }
  }

  onCompanyChange(companyId: string | null): void {
    this.selectedCompanyId.set(companyId);
    this.selectedUserId.set(null);
    this.companyUsers.set([]);
    if (companyId) {
      this.#agenda
        .getSchedulableUsers(companyId)
        .pipe(catchError(() => of([] as SchedulableUser[])))
        .subscribe((u) => this.companyUsers.set(u ?? []));
    }
    this.loadHistory();
  }

  onUserChange(userId: string | null): void {
    this.selectedUserId.set(userId);
    this.loadHistory();
  }

  clearFilters(): void {
    this.selectedCompanyId.set(null);
    this.selectedUserId.set(null);
    this.companyUsers.set([]);
    this.loadHistory();
  }

  // ============================ upcoming ============================

  loadUpcoming(): void {
    this.loadingUpcoming.set(true);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    this.#agenda
      // Pass start-of-today so the backend also returns today's earlier slots.
      .getByFilter('self', 'dayGridMonth', startOfToday)
      .pipe(catchError(() => of([])))
      .subscribe((appointments) => {
        const items: UpcomingCall[] = (appointments ?? [])
          .filter(
            (a) =>
              a.meetingChannel === 'WhatsApp' &&
              (a.base?.statusCode === 0 || a.base?.statusCode === 1) &&
              !!a.startsOnUTC &&
              // Keep today's + future scheduled calls (a passed start time doesn't
              // remove it — the doctor may still need to place the call).
              new Date(a.startsOnUTC).getTime() >= startOfToday.getTime(),
          )
          .map((a) => ({
            appointment: a,
            contactHealthId: a.contact2?.contactHealthId || a.contactHealthId?.id || '',
            name: a.contact2?.name || a.taskCustomerName || 'Paciente',
            startsOnUTC: new Date(a.startsOnUTC).toISOString(),
            permission: 'None' as const,
            permissionLoading: false,
          }))
          .filter((x) => !!x.contactHealthId)
          .sort((a, b) => a.startsOnUTC.localeCompare(b.startsOnUTC));

        this.upcoming.set(items);
        this.loadingUpcoming.set(false);
        this.#loadPermissions(items);
      });
  }

  #loadPermissions(items: UpcomingCall[]): void {
    if (items.length === 0) return;
    const lookups = items.map((it) =>
      this.#data.getPermission(it.contactHealthId).pipe(
        map((p) => ({ id: it.contactHealthId, status: this.#permissionState(p) })),
        catchError(() => of({ id: it.contactHealthId, status: 'None' as const })),
      ),
    );
    forkJoin(lookups).subscribe((results) => {
      const byId = new Map(results.map((r) => [r.id, r.status]));
      this.upcoming.update((list) => list.map((it) => ({ ...it, permission: byId.get(it.contactHealthId) ?? it.permission })));
    });
  }

  #permissionState(p: { permissionStatus?: string; expiresOnUTC?: string | null } | null): 'Approved' | 'Requested' | 'None' {
    if (!p) return 'None';
    if (p.permissionStatus === 'Approved' && (!p.expiresOnUTC || new Date(p.expiresOnUTC) > new Date())) return 'Approved';
    if (p.permissionStatus === 'Requested') return 'Requested';
    return 'None';
  }

  callUpcoming(u: UpcomingCall): void {
    if (this.call.phase() !== 'idle') {
      this.#messageService.add({ severity: 'info', summary: 'Llamada', detail: 'Ya hay una llamada en curso' });
      return;
    }
    void this.call.callPatient(u.contactHealthId, u.name);
  }

  /** Opens the same appointment detail dialog used by the calendar. */
  openDetails(u: UpcomingCall): void {
    this.#dialogService.open(UiAgendaComponent, {
      data: u.appointment,
      header: 'Detalles de la cita',
      width: '50vw',
      modal: true,
      closable: true,
      closeOnEscape: true,
      focusOnShow: false,
      autoZIndex: true,
      baseZIndex: 10000,
      styleClass: 'overflow-visible',
      contentStyle: { overflow: 'visible' },
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });
  }

  requestPermission(u: UpcomingCall): void {
    this.upcoming.update((list) => list.map((it) => (it.contactHealthId === u.contactHealthId ? { ...it, permissionLoading: true } : it)));
    this.#data.requestPermission(u.contactHealthId).subscribe({
      next: () => {
        this.upcoming.update((list) =>
          list.map((it) => (it.contactHealthId === u.contactHealthId ? { ...it, permission: 'Requested', permissionLoading: false } : it)),
        );
        this.#messageService.add({
          severity: 'success',
          summary: 'Permiso de llamada',
          detail: 'Solicitud enviada al paciente por WhatsApp.',
        });
      },
      error: (err) => {
        this.upcoming.update((list) => list.map((it) => (it.contactHealthId === u.contactHealthId ? { ...it, permissionLoading: false } : it)));
        this.#messageService.add({
          severity: 'error',
          summary: 'Permiso de llamada',
          detail: err?.error?.message || 'No fue posible enviar la solicitud',
        });
      },
    });
  }

  // ============================ history ============================

  loadHistory(): void {
    this.loadingHistory.set(true);
    // A specific doctor wins over a company-wide view; no selection = own calls.
    const filter = { userId: this.selectedUserId(), companyId: this.selectedUserId() ? null : this.selectedCompanyId() };
    this.#data.getCalls(1, 50, filter).subscribe({
      next: (res) => {
        this.history.set(res.result ?? []);
        this.loadingHistory.set(false);
      },
      error: () => {
        this.loadingHistory.set(false);
        this.#messageService.add({ severity: 'error', summary: 'Historial', detail: 'No se pudo cargar el historial' });
      },
    });
  }

  directionLabel(c: WhatsAppCallHistory): string {
    return c.direction === 'BUSINESS_INITIATED' ? 'Saliente' : 'Entrante';
  }

  statusSeverity(status?: string): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Missed':
      case 'Failed':
        return 'danger';
      case 'Rejected':
        return 'warn';
      case 'Answered':
        return 'info';
      default:
        return 'secondary';
    }
  }

  statusLabel(status?: string): string {
    switch (status) {
      case 'Completed':
        return 'Completada';
      case 'Missed':
        return 'Perdida';
      case 'Failed':
        return 'Fallida';
      case 'Rejected':
        return 'Rechazada';
      case 'Answered':
        return 'Contestada';
      case 'Ringing':
        return 'Sonando';
      default:
        return status || '—';
    }
  }

  durationLabel(seconds?: number | null): string {
    if (!seconds || seconds < 0) return '—';
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  // ============================ recording playback ============================

  play(c: WhatsAppCallHistory): void {
    const callId = c.base?.id;
    if (!callId) return;
    this.#stopPlayback();
    this.playingCallId.set(callId);
    // Fetched as a blob through the backend (authenticated); the storage account is
    // firewalled, so a direct SAS URL from the browser would be blocked.
    this.#data.getRecordingBlob(callId).subscribe({
      next: (blob) => {
        this.#objectUrl = URL.createObjectURL(blob);
        this.#audio = new Audio(this.#objectUrl);
        this.#audio.onended = () => this.stop();
        void this.#audio.play().catch(() => this.stop());
      },
      error: () => {
        this.playingCallId.set(null);
        this.#messageService.add({ severity: 'error', summary: 'Grabación', detail: 'No se pudo reproducir la grabación' });
      },
    });
  }

  stop(): void {
    this.#stopPlayback();
    this.playingCallId.set(null);
  }

  #stopPlayback(): void {
    if (this.#audio) {
      this.#audio.pause();
      this.#audio = null;
    }
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = null;
    }
  }
}
